// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  ScanProxyApi,
  ServerConfiguration,
  createConfiguration,
  RequestContext,
  ResponseContext,
  Middleware,
} from '@digitalasset/validator-scan-proxy-api-ts-client';
import React, { useMemo } from 'react';
import { BaseApiMiddleware } from '../utils/BaseApiMiddleware';
import { ScanProxyApiContext } from './ScanProxyApiContext';
import { useUserState } from './UserProvider';
import { OpenAPILoggingMiddleware } from '../utils/OpenApiLoggingMiddleware';

export interface ScanProxyApiProps {
  validatorUrl: string;
}

class ApiMiddleware
  extends BaseApiMiddleware<RequestContext, ResponseContext>
  implements Middleware {}

export const ScanProxyApiProvider = ({
  validatorUrl,
  children,
}: React.PropsWithChildren<ScanProxyApiProps>) => {
  const { loggedInUser } = useUserState();
  const selectedToken = loggedInUser?.userAccessToken ?? '';
  const client: ScanProxyApi | undefined = useMemo(() => {
    const configuration = createConfiguration({
      baseServer: new ServerConfiguration(validatorUrl, {}),
      promiseMiddleware: [
        new ApiMiddleware(selectedToken),
        new OpenAPILoggingMiddleware('scan-proxy'),
      ],
    });

    return new ScanProxyApi(configuration);
  }, [validatorUrl, selectedToken]);

  return <ScanProxyApiContext.Provider value={client}>{children}</ScanProxyApiContext.Provider>;
};
